/**
 * Emit hosts for the `auth` analytics group — 6 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 *
 * Only the store-free half of the auth cluster lives here. An effect that reads
 * `store.getState().auth?.authProvider` at emit time — `identify`,
 * `signInClicked`, the widget-auth pair — cannot move: a free function has no
 * model dispatch and this file must not reach the store, so the provider would
 * have to arrive as an argument, which changes the payload contract at every
 * call site. They stay in the model until auth state has a single store-free
 * source of truth to read the provider from.
 */
import { GET_ONBOARDING_V2_VARIANT } from '@/features/flags';
import { segmentClient } from '@/lib/analytics/legacy-sender-context';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { clearRemoteConfigCustomSignals } from '@/lib/firebase/custom-signals';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type AuthType,
  mapAuthProviderToProvider,
  mapSignInMethodToAuthType,
  type SignInMethod,
} from '@/models/analytics/features/user';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type AuthProvider } from '@/models/authModel';
import { logger } from '@/utils/logger/logger';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify as AmplitudeIdentify } from '@amplitude/analytics-browser';
import { setAnalyticsContext } from '@heidi/runtime/analytics';
import { type TeamModel } from '@heidi/workspace/contracts';
import posthog from 'posthog-js';

export function resetIdentity() {
  void segmentClient.reset();
  // Clear every RC custom signal so the next session is not targeted as the prior user.
  // The Firebase SDK loads lazily inside the custom-signals helper, not at import time.
  clearRemoteConfigCustomSignals();
}

export function setActiveTeam(team: TeamModel) {
  // DEV-4675 - remove user role list and change joined_team_members to a num type
  const teamModified = {
    ...team,
    joined_team_members: team.joined_team_members?.length ?? 0,
    team_members: team.team_members?.map((member) => ({
      ...member,
      user_role_list: member.user_role_list?.map((role) => ({
        id: role.id,
        name: role.name,
        config: role.config,
      })),
    })),
  };

  // Store the team id in the @heidi/runtime/analytics context registry — the
  // single teamId source for every destination. The caller only reaches here
  // with the team selected for the active workspace (selectActiveTeamId), so
  // this id is always correct for the current workspace; consumers read it
  // directly with no per-event guard.
  setAnalyticsContext({ teamId: team.id });
  // This is for autocaptured PH events
  posthog.group('team', team.id, teamModified);
  // This is for our custom events we send via segment
  void segmentClient.group(team.id, teamModified);

  // Set Amplitude group for team-based analytics
  try {
    amplitude.setGroup('team', team.id);
    const groupIdentifyObj = new AmplitudeIdentify();
    groupIdentifyObj.set('team_name', team.name ?? '');
    groupIdentifyObj.set('joined_team_members', teamModified.joined_team_members);
    amplitude.groupIdentify('team', team.id, groupIdentifyObj);
  } catch (error) {
    logger.error('[Amplitude] Failed to set group identity:', error);
  }
}

export function setAuthTypeUserProperty(authType: AuthType) {
  const identifyObj = new AmplitudeIdentify();
  identifyObj.set('auth_type', authType);
  amplitude.identify(identifyObj);
}

export function trackNativeAuthenticated() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NATIVE_AUTHENTICATED,
    },
  });
}

export function trackSignInSuccessfulWebAuth(input: {
  auth_provider: AuthProvider;
  sign_in_method: SignInMethod;
}) {
  // Track the sign in event
  track({
    event_name: TrackingEventName.UserSignedIn,
    props: {
      auth_provider: input.auth_provider,
      sign_in_method: input.sign_in_method,
      provider: mapAuthProviderToProvider(input.auth_provider),
      auth_type: mapSignInMethodToAuthType(input.sign_in_method),
    },
    options: { amplitude: false, posthog: true },
  });
}

export function trackSignUpSuccessfulWebAuth(input: {
  auth_provider: AuthProvider;
  sign_in_method: SignInMethod;
}) {
  track({
    event_name: TrackingEventName.UserSignedUp,
    props: {
      auth_provider: input.auth_provider,
      sign_in_method: input.sign_in_method,
      provider: mapAuthProviderToProvider(input.auth_provider),
      auth_type: mapSignInMethodToAuthType(input.sign_in_method),
      onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
    },
    options: { amplitude: false, posthog: true },
  });
}
